import { getAuthToken } from '../utils/auth';
import { handleUnauthorized } from '../utils/errorHandler';

const API_BASE_URL = (import.meta as any).env.VITE_BACKEND_BASE_URL;

interface AuthResponse {
  success: boolean;
  message?: string;
}

interface YahooAuthResponse extends AuthResponse {
  authUrl?: string;
}

interface PlatformStatusResponse {
  isOnboarded: boolean;
}

// Yahoo Authentication Functions
export const initYahooAuth = async (redirectUri: string): Promise<YahooAuthResponse> => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(`${API_BASE_URL}/auth/yahoo/init`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ redirectUri }),
  });

  if (response.status === 401) {
    handleUnauthorized();
    throw new Error('UNAUTHORIZED');
  }

  if (!response.ok) {
    throw new Error('Failed to initiate Yahoo authentication');
  }

  return response.json();
};

export const handleYahooCallback = async (code: string, state: string): Promise<AuthResponse> => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(`${API_BASE_URL}/auth/yahoo/callback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ code, state }),
  });

  if (response.status === 401) {
    handleUnauthorized();
    throw new Error('UNAUTHORIZED');
  }

  if (!response.ok) {
    throw new Error('Failed to complete Yahoo authentication');
  }

  return response.json();
};

export const getYahooStatus = async (): Promise<PlatformStatusResponse> => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(`${API_BASE_URL}/auth/yahoo/status`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (response.status === 401) {
    handleUnauthorized();
    throw new Error('UNAUTHORIZED');
  }

  if (!response.ok) {
    throw new Error('Failed to check Yahoo status');
  }

  return response.json();
};

export const disconnectYahoo = async (): Promise<AuthResponse> => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(`${API_BASE_URL}/auth/yahoo/disconnect`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (response.status === 401) {
    handleUnauthorized();
    throw new Error('UNAUTHORIZED');
  }

  if (!response.ok) {
    throw new Error('Failed to disconnect Yahoo account');
  }

  return response.json();
};

// Sleeper Authentication Functions
export const connectSleeper = async (username: string): Promise<AuthResponse> => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(`${API_BASE_URL}/auth/sleeper/connect`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ username: username.trim() }),
  });

  if (response.status === 401) {
    handleUnauthorized();
    throw new Error('UNAUTHORIZED');
  }

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to connect Sleeper account');
  }

  return response.json();
};

export const getSleeperStatus = async (): Promise<PlatformStatusResponse> => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(`${API_BASE_URL}/auth/sleeper/status`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (response.status === 401) {
    handleUnauthorized();
    throw new Error('UNAUTHORIZED');
  }

  if (!response.ok) {
    throw new Error('Failed to check Sleeper status');
  }

  return response.json();
};

export const disconnectSleeper = async (): Promise<AuthResponse> => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(`${API_BASE_URL}/auth/sleeper/disconnect`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (response.status === 401) {
    handleUnauthorized();
    throw new Error('UNAUTHORIZED');
  }

  if (!response.ok) {
    throw new Error('Failed to disconnect Sleeper account');
  }

  return response.json();
};

// Combined status check for both platforms
export const getPlatformStatuses = async (): Promise<{
  yahoo: PlatformStatusResponse;
  sleeper: PlatformStatusResponse;
}> => {
  const [yahooStatus, sleeperStatus] = await Promise.all([
    getYahooStatus(),
    getSleeperStatus(),
  ]);

  return {
    yahoo: yahooStatus,
    sleeper: sleeperStatus,
  };
};